// src/components/Onboarding/BeforeAfterSlider.js

import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * BeforeAfterSlider - A touch/mouse draggable before/after image comparison component
 * 
 * GESTURE DETECTION (Industry Standard):
 * This component implements direction-aware gesture detection to distinguish between
 * horizontal slider movement and vertical page scrolling. This is the same pattern used
 * by Instagram carousels, Google Maps, and photo gallery apps.
 * 
 * How it works:
 * 1. On pointer/touch down, we enter a "pending" state and record start position
 * 2. We wait for movement to exceed a threshold (10px) before deciding direction
 * 3. If movement is more horizontal than vertical → control the slider, prevent scroll
 * 4. If movement is more vertical than horizontal → allow native scroll, ignore for slider
 * 5. The direction is locked until the gesture ends (pointer up)
 * 
 * Props:
 * - beforeImage: URL/data URL for the "before" image (left side)
 * - afterImage: URL/data URL for the "after" image (right side)
 * - beforeLabel: Optional label for before image (default: "Before")
 * - afterLabel: Optional label for after image (default: "After")
 * - initialPosition: Initial divider position as percentage (default: 50)
 * - className: Additional CSS classes for the container
 */

// Gesture direction detection threshold in pixels
// Movement must exceed this before we decide horizontal vs vertical
const DIRECTION_LOCK_THRESHOLD = 10;

export default function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  initialPosition = 50,
  className = '',
}) {
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [imagesLoaded, setImagesLoaded] = useState({ before: false, after: false });
  
  // Gesture state management
  // 'idle' - no active gesture
  // 'pending' - touch started, waiting to determine direction
  // 'horizontal' - locked to horizontal (controls slider)
  // 'vertical' - locked to vertical (allows page scroll)
  const [gestureState, setGestureState] = useState('idle');
  
  // Store gesture start position for direction detection
  const gestureStartRef = useRef({ x: 0, y: 0 });

  // Calculate slider position from pointer X coordinate
  const calculatePosition = useCallback((clientX) => {
    if (!containerRef.current) return sliderPosition;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    // Clamp between 5% and 95% to keep handle visible
    return Math.max(5, Math.min(95, percentage));
  }, [sliderPosition]);

  // Handle pointer down - start pending gesture, don't prevent default yet
  const handlePointerDown = useCallback((e) => {
    // Store start position for direction detection
    gestureStartRef.current = { x: e.clientX, y: e.clientY };
    setGestureState('pending');
    
    // For mouse events, we can be more aggressive since there's no scroll conflict
    if (e.pointerType === 'mouse') {
      e.preventDefault();
      setGestureState('horizontal');
      setSliderPosition(calculatePosition(e.clientX));
    }
  }, [calculatePosition]);

  // Handle pointer move - detect direction or update slider
  const handlePointerMove = useCallback((e) => {
    if (gestureState === 'idle') return;
    
    const deltaX = Math.abs(e.clientX - gestureStartRef.current.x);
    const deltaY = Math.abs(e.clientY - gestureStartRef.current.y);
    
    // Still in pending state - check if we've moved enough to determine direction
    if (gestureState === 'pending') {
      const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (totalMovement >= DIRECTION_LOCK_THRESHOLD) {
        // Determine direction based on which axis has more movement
        // Using a 1:1 ratio (45 degrees) as the cutoff is industry standard
        if (deltaX > deltaY) {
          // More horizontal movement - lock to slider control
          setGestureState('horizontal');
          e.preventDefault();
          setSliderPosition(calculatePosition(e.clientX));
        } else {
          // More vertical movement - allow native scroll
          setGestureState('vertical');
          // Don't prevent default - let the page scroll naturally
        }
      }
      return;
    }
    
    // Already locked to horizontal - control the slider
    if (gestureState === 'horizontal') {
      e.preventDefault();
      setSliderPosition(calculatePosition(e.clientX));
    }
    
    // If locked to vertical, do nothing - native scroll handles it
  }, [gestureState, calculatePosition]);

  // Handle pointer up - reset gesture state
  const handlePointerUp = useCallback(() => {
    setGestureState('idle');
  }, []);

  // Add/remove global listeners for drag tracking
  useEffect(() => {
    if (gestureState !== 'idle') {
      window.addEventListener('pointermove', handlePointerMove, { passive: false });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    }
    
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [gestureState, handlePointerMove, handlePointerUp]);

  // Track image loading
  const handleBeforeLoad = useCallback(() => {
    setImagesLoaded(prev => ({ ...prev, before: true }));
  }, []);

  const handleAfterLoad = useCallback(() => {
    setImagesLoaded(prev => ({ ...prev, after: true }));
  }, []);

  const bothImagesLoaded = imagesLoaded.before && imagesLoaded.after;

  // Determine if we're actively controlling the slider (for cursor feedback)
  const isSliderActive = gestureState === 'horizontal';

  return (
    <div 
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-2xl select-none ${className}`}
      style={{ 
        aspectRatio: '1 / 1',
        cursor: isSliderActive ? 'grabbing' : 'grab',
        // touch-action: pan-y allows vertical scrolling while we handle horizontal
        // This is a CSS fallback that works alongside our JS gesture detection
        touchAction: 'pan-y',
      }}
      onPointerDown={handlePointerDown}
    >
      {/* Loading state */}
      {!bothImagesLoaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* After image (right side, full width, shown behind) */}
      <div className="absolute inset-0">
        <img
          src={afterImage}
          alt={afterLabel}
          className="w-full h-full object-cover"
          onLoad={handleAfterLoad}
          draggable={false}
        />
      </div>

      {/* Before image (left side, clipped) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ 
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
        }}
      >
        <img
          src={beforeImage}
          alt={beforeLabel}
          className="w-full h-full object-cover"
          onLoad={handleBeforeLoad}
          draggable={false}
        />
      </div>

      {/* Vertical divider line */}
      <div 
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
        style={{ 
          left: `${sliderPosition}%`,
          transform: 'translateX(-50%)',
          boxShadow: '0 0 8px rgba(0,0,0,0.5)',
        }}
      />

      {/* Draggable handle */}
      <div
        className="absolute top-1/2 w-10 h-10 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
        style={{ 
          left: `${sliderPosition}%`,
        }}
      >
        <div 
          className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center"
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.8)',
          }}
        >
          {/* Left/Right arrows */}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-gray-700">
            <path d="M7 10L4 7M4 7L7 4M4 7H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 10L16 13M16 13L13 16M16 13H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {/* Labels */}
      {bothImagesLoaded && (
        <>
          {/* Before label (left) */}
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-white text-xs font-medium"
            style={{
              opacity: sliderPosition > 15 ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
          >
            {beforeLabel}
          </div>

          {/* After label (right) */}
          <div 
            className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-white text-xs font-medium"
            style={{
              opacity: sliderPosition < 85 ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
          >
            {afterLabel}
          </div>
        </>
      )}
    </div>
  );
}
